import { useState, useEffect, useCallback } from "react";
import { useGetIdentity } from "ra-core";
import { RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { BC_ENVIRONMENTS } from "../misc/bcEnvironment";

type SyncLog = {
  id: number;
  synced_at: string;
  resource: string;
  records_upserted: number;
  records_archived: number;
  records_failed: number;
  triggered_by: string;
  delta_since: string | null;
};

const RESOURCES = [
  "customers",
  "contacts",
  "salespeople",
  "interactions",
  "segments",
] as const;

type SyncResource = (typeof RESOURCES)[number];

/** Per-company resources — invoke one environment per request to avoid worker cancels. */
const MULTI_ENV_RESOURCES = new Set<SyncResource>([
  "customers",
  "contacts",
  "segments",
  "interactions",
]);

/** Prefer SE then FI before DK so a DK timeout does not block other environments. */
const SYNC_ENV_PRIORITY: Record<string, number> = { SE: 0, FI: 1, DK: 2 };
const SYNC_ENV_ORDER = [...BC_ENVIRONMENTS]
  .map((e) => e.code)
  .sort(
    (a, b) =>
      (SYNC_ENV_PRIORITY[a] ?? 99) - (SYNC_ENV_PRIORITY[b] ?? 99),
  );

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

async function readBcSyncErrorMessage(
  error: unknown,
  data?: unknown,
): Promise<string> {
  const formatUnknown = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message;
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      if (typeof record.message === "string" && record.message) {
        return record.message;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  if (data && typeof data === "object") {
    const payload = data as { errors?: unknown[]; success?: boolean };
    if (payload.errors?.length) {
      return payload.errors.map(formatUnknown).join(" · ");
    }
    if (payload.success === false) return "Sync failed";
  }

  const fallback = formatUnknown(error);
  const context = (error as { context?: Response })?.context;
  if (context && typeof context.json === "function") {
    try {
      const payload = await context.json();
      if (payload && typeof payload === "object") {
        const errors = (payload as { errors?: unknown[] }).errors;
        if (errors?.length) return errors.map(formatUnknown).join(" · ");
        if ("message" in payload) return formatUnknown(payload.message);
        if ("error" in payload) return formatUnknown(payload.error);
        if ("msg" in payload) return formatUnknown(payload.msg);
      }
    } catch {
      // ignore parse errors
    }
  }

  if (/WorkerRequestCancelled|cancelled by supervisor/i.test(fallback)) {
    return "Sync timed out (worker cancelled). Sync one resource/environment at a time and retry.";
  }

  return fallback || "Sync failed";
}

type InvokeResult = {
  results: Record<
    string,
    {
      upserted?: number;
      failed?: number;
      archived?: number;
      incomplete?: boolean;
    }
  >;
};

async function invokeBcSync(body: {
  resource: string;
  full?: boolean;
  environment?: string;
}): Promise<InvokeResult> {
  const { data, error } = await getSupabaseClient().functions.invoke("bc-sync", {
    body,
  });
  if (error) {
    throw new Error(await readBcSyncErrorMessage(error, data));
  }
  if (data?.success === false) {
    throw new Error(await readBcSyncErrorMessage(null, data));
  }
  return { results: data?.results ?? {} };
}

/** Contacts may return incomplete=true under a time budget; retry until done. */
async function invokeBcSyncUntilComplete(body: {
  resource: string;
  full?: boolean;
  environment?: string;
}): Promise<InvokeResult> {
  const merged: InvokeResult["results"] = {};
  const maxRounds = body.resource === "contacts" ? 30 : 1;
  for (let round = 0; round < maxRounds; round++) {
    const { results } = await invokeBcSync({
      ...body,
      // Only the first contacts round should clear the cursor on full relink.
      full: body.full && round === 0,
    });
    for (const [key, stats] of Object.entries(results)) {
      const prev = merged[key];
      merged[key] = {
        upserted: (prev?.upserted ?? 0) + (stats.upserted ?? 0),
        failed: (prev?.failed ?? 0) + (stats.failed ?? 0),
        archived: (prev?.archived ?? 0) + (stats.archived ?? 0),
        incomplete: stats.incomplete,
      };
    }
    const incomplete = Object.values(results).some((s) => s.incomplete);
    if (!incomplete) break;
  }
  return { results: merged };
}

function summarizeResults(
  results: Record<
    string,
    {
      upserted?: number;
      failed?: number;
      archived?: number;
      incomplete?: boolean;
    }
  >,
): string {
  return Object.entries(results)
    .map(([r, s]) => {
      const parts = [`${r}: ${s.upserted ?? 0} upserted`];
      if (s.archived) parts.push(`${s.archived} archived`);
      if (s.failed) parts.push(`${s.failed} failed`);
      if (s.incomplete) parts.push("incomplete");
      return parts.join(", ");
    })
    .join(" · ");
}

export const BCSyncPanel = () => {
  const { data: identity } = useGetIdentity();
  if (!identity?.administrator) return null;
  return <BCSyncPanelContent />;
};

const BCSyncPanelContent = () => {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    const { data } = await getSupabaseClient()
      .from("bc_sync_log")
      .select("*")
      .order("synced_at", { ascending: false })
      .limit(50);
    if (data) setLogs(data as SyncLog[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const lastFor = (resource: string) =>
    logs.find(
      (l) => l.resource === resource || l.resource.startsWith(`${resource}:`),
    );

  const handleSync = async (
    resource: "all" | SyncResource,
    options?: { full?: boolean },
  ) => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    const full = options?.full ?? false;
    const merged: Record<
      string,
      { upserted?: number; failed?: number; archived?: number }
    > = {};
    const stepErrors: string[] = [];

    const resourcesToRun: SyncResource[] =
      resource === "all"
        ? ["salespeople", "customers", "contacts", "segments", "interactions"]
        : [resource];

    try {
      for (const res of resourcesToRun) {
        if (MULTI_ENV_RESOURCES.has(res)) {
          for (const environment of SYNC_ENV_ORDER) {
            try {
              const { results } = await invokeBcSyncUntilComplete({
                resource: res,
                full,
                environment,
              });
              Object.assign(merged, results);
            } catch (e: unknown) {
              const msg =
                e instanceof Error
                  ? e.message
                  : await readBcSyncErrorMessage(e);
              stepErrors.push(`${res}:${environment}: ${msg}`);
            }
          }
        } else {
          try {
            const { results } = await invokeBcSyncUntilComplete({
              resource: res,
              full,
            });
            Object.assign(merged, results);
          } catch (e: unknown) {
            const msg =
              e instanceof Error ? e.message : await readBcSyncErrorMessage(e);
            stepErrors.push(`${res}: ${msg}`);
          }
        }
      }

      const summary = summarizeResults(merged);
      if (stepErrors.length > 0) {
        setSyncError(stepErrors.join(" · "));
        if (summary) setSyncResult(summary);
      } else {
        setSyncResult(summary || "Sync complete");
      }
      await fetchLogs();
    } catch (e: unknown) {
      setSyncError(
        e instanceof Error ? e.message : await readBcSyncErrorMessage(e),
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card id="bc-sync">
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-muted-foreground">
            Business Central Sync
          </h2>
          <Button
            type="button"
            onClick={() => handleSync("all")}
            disabled={syncing}
            size="sm"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>

        {syncResult && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {syncResult}
          </div>
        )}
        {syncError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {syncError}
          </div>
        )}

        <Separator />

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading sync status…</p>
        ) : (
          <div className="space-y-4">
            {RESOURCES.map((resource) => {
              const last = lastFor(resource);
              return (
                <div
                  key={resource}
                  className="flex items-start justify-between gap-4"
                >
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium capitalize">{resource}</p>
                    {last ? (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span>{formatDate(last.synced_at)}</span>
                        <span>·</span>
                        <span className="font-mono">{last.resource}</span>
                        <span>·</span>
                        <span>{last.records_upserted} upserted</span>
                        {last.records_archived > 0 && (
                          <>
                            <span>·</span>
                            <span>{last.records_archived} archived</span>
                          </>
                        )}
                        {last.records_failed > 0 && (
                          <>
                            <span>·</span>
                            <span className="text-destructive">
                              {last.records_failed} failed
                            </span>
                          </>
                        )}
                        <span>·</span>
                        <span>{last.triggered_by}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Never synced
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {resource === "customers" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(resource, { full: true })}
                        disabled={syncing}
                        title="Re-fetch all BC customers and soft-archive ones deleted in BC"
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                        />
                        Full reconcile
                      </Button>
                    )}
                    {resource === "contacts" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(resource, { full: true })}
                        disabled={syncing}
                        title="Re-fetch all BC contacts and re-link companies"
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                        />
                        Full relink
                      </Button>
                    )}
                    {resource === "segments" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(resource, { full: true })}
                        disabled={syncing}
                        title="Re-fetch all BC segments and rebuild contact sector memberships"
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                        />
                        Full relink
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(resource)}
                      disabled={syncing}
                    >
                      <RefreshCw
                        className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                      />
                      Sync
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
